import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Settings, BarChart3 } from "lucide-react";
import { LocaleLink } from "@/components/LocaleLink";

type Tab = "new" | "in_review" | "claimed" | "completed" | "declined" | "profile" | "payment";

interface DashboardNavProps {
  activeTab: Tab;
  onTabChange: (tab: Tab) => void;
  counts: {
    new: number;
    in_review: number;
    claimed: number;
    completed: number;
    declined: number;
  };
  showPaymentWarning?: boolean;
}

const DashboardNav = ({ activeTab, onTabChange, counts, showPaymentWarning }: DashboardNavProps) => {
  const { user } = useAuth();
  const [isOperator, setIsOperator] = useState(false);

  useEffect(() => {
    if (!user) return;
    supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .in("role", ["admin", "operator", "operator_readonly"])
      .then(({ data }) => {
        setIsOperator(!!data && data.length > 0);
      });
  }, [user]);

  const navItems: { id: Tab; label: string; count?: number; warning?: boolean }[] = [
    { id: "new", label: "Neu", count: counts.new },
    { id: "in_review", label: "Angenommen", count: counts.in_review },
    { id: "claimed", label: "Reklamiert", count: counts.claimed },
    { id: "declined", label: "Abgelehnt", count: counts.declined },
    { id: "completed", label: "Abgeschlossen", count: counts.completed },
    { id: "payment", label: "Zahlung & Abrechnung", warning: showPaymentWarning },
    { id: "profile", label: "Profil" },
  ];

  return (
    <nav className="space-y-2">
      {navItems.map((item) => (
        <button
          key={item.id}
          onClick={() => onTabChange(item.id)}
          className={cn(
            "w-full text-left px-4 py-3 text-sm transition-all duration-200 border-l-2",
            activeTab === item.id
              ? "border-accent text-foreground bg-accent/5"
              : "border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary/50"
          )}
        >
          <div className="flex items-center justify-between">
            <span>{item.label}</span>
            {item.count !== undefined && item.count > 0 && (
              <span
                className={cn(
                  "text-xs px-2 py-0.5 rounded-sm",
                  activeTab === item.id
                    ? "bg-accent/20 text-accent"
                    : "bg-muted text-muted-foreground"
                )}
              >
                {item.count}
              </span>
            )}
            {item.warning && (
              <span className="w-2 h-2 rounded-full bg-destructive" />
            )}
          </div>
        </button>
      ))}

      <div className="h-px bg-border my-3" />
      <LocaleLink
        to="/provider/analytics"
        className="flex items-center gap-2.5 w-full px-4 py-3 text-sm text-muted-foreground border-l-2 border-transparent hover:border-accent hover:bg-accent/5 transition-all duration-200"
      >
        <BarChart3 className="w-4 h-4" />
        <span>Analytics & KPIs</span>
      </LocaleLink>

      {isOperator && (
        <>
          <LocaleLink
            to="/operator"
            className="flex items-center gap-2.5 w-full px-4 py-3 text-sm text-accent border-l-2 border-transparent hover:border-accent hover:bg-accent/5 transition-all duration-200"
          >
            <Settings className="w-4 h-4" />
            <span>Operator Console</span>
          </LocaleLink>
        </>
      )}
    </nav>
  );
};

export default DashboardNav;
